// src/pages/financeiro/RecebimentoDetalhesModal.js
import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../components/modalForm/StackedBootstrapModal';
import { formatCurrency } from '../../utils/numberUtils';
import { formatDate } from '../../utils/dateTimeUtils';
import { useMessage } from '../../contexts/geral/MessageContext';
import { buscarDetalhesRecebimento } from '../../services/financeiro/RecebimentoService';
import {
    STATUS_RECEBIMENTO,
    obterClasseStatusRecebimento,
    obterDescricaoStatusRecebimento,
} from '../../models/financeiro/Recebimento';
import PagamentoRecebimentoModal from './PagamentoRecebimentoModal';
import ParcelaPagamentosModal from './ParcelaPagamentosModal';

const descricaoDocumentos = (recebimento) => (recebimento?.documentos || [])
    .map((documento) => documento.numero
        ? `${documento.modeloDescricao || 'Documento'} ${documento.numero}`
        : String(documento.notaId))
    .join(' / ') || '-';

const RecebimentoDetalhesModal = ({ show, recebimentoId, onHide, onAlterado, onImprimir }) => {
    const { showMessage } = useMessage();
    const [recebimento, setRecebimento] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [parcelasSelecionadas, setParcelasSelecionadas] = useState([]);
    const [showPagamento, setShowPagamento] = useState(false);
    const [parcelaDetalhesId, setParcelaDetalhesId] = useState(null);

    const carregar = async () => {
        if (!recebimentoId) return;
        setCarregando(true);
        try {
            const detalhes = await buscarDetalhesRecebimento(recebimentoId);
            setRecebimento(detalhes);
            setParcelasSelecionadas((atuais) => atuais.filter((id) =>
                detalhes.recebimentoParcelas.some((parcela) => parcela.id === id
                    && parcela.statusRec !== STATUS_RECEBIMENTO.PAGO
                    && parcela.statusRec !== STATUS_RECEBIMENTO.CANCELADO)));
        } catch (error) {
            showMessage(error?.message || 'Não foi possível carregar o recebimento.', 'danger');
            onHide();
        } finally {
            setCarregando(false);
        }
    };

    useEffect(() => {
        if (!show) return;
        setRecebimento(null);
        setParcelasSelecionadas([]);
        setParcelaDetalhesId(null);
        setShowPagamento(false);
        carregar();
    }, [show, recebimentoId]);

    const parcelasElegiveis = useMemo(() => (recebimento?.recebimentoParcelas || []).filter(
        (parcela) => parcela.statusRec !== STATUS_RECEBIMENTO.PAGO
            && parcela.statusRec !== STATUS_RECEBIMENTO.CANCELADO
            && Number(parcela.saldo) > 0
    ), [recebimento]);
    const selecionadas = useMemo(() => parcelasElegiveis.filter(
        (parcela) => parcelasSelecionadas.includes(parcela.id)
    ), [parcelasElegiveis, parcelasSelecionadas]);
    const todasSelecionadas = parcelasElegiveis.length > 0
        && parcelasElegiveis.every((parcela) => parcelasSelecionadas.includes(parcela.id));
    const parcelaDetalhes = (recebimento?.recebimentoParcelas || [])
        .find((parcela) => parcela.id === parcelaDetalhesId);

    const atualizarRecebimento = async (atualizado) => {
        setRecebimento(atualizado);
        setParcelasSelecionadas([]);
        await onAlterado?.(atualizado);
    };

    const alternarTodas = () => setParcelasSelecionadas(
        todasSelecionadas ? [] : parcelasElegiveis.map((parcela) => parcela.id)
    );

    return (
        <>
            <Modal show={show} onHide={onHide} centered size="xl" className="recebimento-detalhes-modal">
                <Modal.Header closeButton>
                    <div>
                        <Modal.Title>Recebimento #{recebimentoId}</Modal.Title>
                        <small className="text-muted">Detalhes, parcelas e pagamentos</small>
                    </div>
                </Modal.Header>
                <Modal.Body>
                    {carregando && <div className="text-center py-5"><div className="spinner-border text-primary" /><p className="mt-2 text-muted">Carregando recebimento...</p></div>}
                    {!carregando && recebimento && (
                        <>
                            <div className="recebimento-identificacao mb-3">
                                <div><span>Cliente</span><strong>{recebimento.clienteNome || '-'}</strong></div>
                                <div><span>Documentos</span><strong>{descricaoDocumentos(recebimento)}</strong></div>
                                <div><span>Emissão</span><strong>{formatDate(recebimento.dataCriacao)}</strong></div>
                                <div><span>Forma prevista</span><strong>{recebimento.formaPagamentoDescricao || '-'}</strong></div>
                            </div>
                            <div className="recebimento-cards mb-4">
                                <div className="erp-card"><span>Valor total</span><strong>{formatCurrency(recebimento.valorTotal)}</strong></div>
                                <div className="erp-card pago"><span>Valor pago</span><strong>{formatCurrency(recebimento.valorPago)}</strong></div>
                                <div className="erp-card saldo"><span>Saldo em aberto</span><strong>{formatCurrency(recebimento.saldo)}</strong></div>
                                <div className="erp-card"><span>Status</span><strong className={`status-badge ${obterClasseStatusRecebimento(recebimento.statusRec)}`}>{obterDescricaoStatusRecebimento(recebimento.statusRec)}</strong></div>
                            </div>

                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <div>
                                    <h6 className="mb-0">Parcelas</h6>
                                    <small className="text-muted">Selecione uma ou mais parcelas para registrar o pagamento.</small>
                                </div>
                                <span className="badge bg-light text-dark border">{recebimento.quantidadeParcelasPagas}/{recebimento.quantidadeParcelas} pagas</span>
                            </div>
                            <div className="table-responsive parcelas-grid">
                                <table className="table table-sm table-hover align-middle mb-0">
                                    <thead>
                                        <tr>
                                            <th><input type="checkbox" checked={todasSelecionadas} onChange={alternarTodas} disabled={!parcelasElegiveis.length} aria-label="Selecionar todas as parcelas disponíveis" /></th>
                                            <th>Parcela</th><th>Vencimento</th><th className="text-end">Valor</th><th className="text-end">Pago</th><th className="text-end">Saldo</th><th>Status</th><th className="text-center">Atraso</th><th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {recebimento.recebimentoParcelas.map((parcela) => {
                                            const elegivel = parcelasElegiveis.some((item) => item.id === parcela.id);
                                            return (
                                                <tr key={parcela.id} className={parcela.diasAtraso > 0 ? 'parcela-atrasada' : ''}>
                                                    <td><input type="checkbox" disabled={!elegivel}
                                                        checked={parcelasSelecionadas.includes(parcela.id)}
                                                        onChange={() => setParcelasSelecionadas((atuais) => atuais.includes(parcela.id)
                                                            ? atuais.filter((id) => id !== parcela.id)
                                                            : [...atuais, parcela.id])} /></td>
                                                    <td className="fw-bold">{parcela.numParcela}</td>
                                                    <td>{formatDate(parcela.dataVencimento)}</td>
                                                    <td className="text-end">{formatCurrency(parcela.valorParcela)}</td>
                                                    <td className="text-end text-success">{formatCurrency(parcela.valorPago)}</td>
                                                    <td className="text-end fw-bold">{formatCurrency(parcela.saldo)}</td>
                                                    <td><span className={`status-badge ${obterClasseStatusRecebimento(parcela.statusRec)}`}>{obterDescricaoStatusRecebimento(parcela.statusRec)}</span></td>
                                                    <td className="text-center">{parcela.diasAtraso > 0 ? `${parcela.diasAtraso} dia(s)` : '-'}</td>
                                                    <td className="text-end"><button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setParcelaDetalhesId(parcela.id)}>Detalhes</button></td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="justify-content-between">
                    <div className="d-flex gap-2">
                        <button type="button" className="btn btn-outline-info" disabled={!recebimento?.valorPago}
                            onClick={() => onImprimir?.((recebimento?.recebimentoParcelas || []).filter((parcela) => parcela.valorPago > 0))}>
                            Imprimir comprovante
                        </button>
                    </div>
                    <div className="d-flex gap-2">
                        <button type="button" className="btn btn-secondary" onClick={onHide}>Fechar</button>
                        <button type="button" className="btn btn-success px-4" disabled={!selecionadas.length}
                            onClick={() => setShowPagamento(true)}>
                            Pagar {selecionadas.length ? `(${selecionadas.length})` : ''}
                        </button>
                    </div>
                </Modal.Footer>
            </Modal>

            <PagamentoRecebimentoModal show={showPagamento} recebimento={recebimento}
                parcelas={selecionadas} onHide={() => setShowPagamento(false)} onSuccess={atualizarRecebimento} />
            <ParcelaPagamentosModal show={!!parcelaDetalhes} parcela={parcelaDetalhes}
                onHide={() => setParcelaDetalhesId(null)}
                onRecebimentoAtualizado={atualizarRecebimento}
                onParcelaExcluida={async () => { setParcelaDetalhesId(null); await carregar(); await onAlterado?.(); }} />
        </>
    );
};

export default RecebimentoDetalhesModal;
